import React, { useState } from 'react';
import { createContact, uploadFile } from '../services/api';
import { logError } from '../lib/logError';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { Phone, Mail, Clock, Send, Upload, CheckCircle } from 'lucide-react';
import PrivacyCheckbox from '../components/PrivacyCheckbox';

const ContattiPage = () => {
  const [formData, setFormData] = useState({ nome: '', email: '', oggetto: '', messaggio: '' });
  const [files, setFiles] = useState([]);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.email || !formData.messaggio) {
      toast({ title: "Errore", description: "Compila tutti i campi obbligatori", variant: "destructive" });
      return;
    }
    if (!privacyAccepted) {
      toast({ title: "Errore", description: "Devi accettare l'informativa sulla privacy per procedere", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      let fileUrls = [];
      for (const file of files) {
        try {
          const res = await uploadFile(file);
          fileUrls.push(res.url);
        } catch (e) {
          logError('[contatti] upload file fallito', e);
          toast({ title: "Attenzione", description: `Impossibile caricare ${file.name}`, variant: "destructive" });
        }
      }
      await createContact({ ...formData, files: fileUrls });
      setSent(true);
      toast({ title: "Messaggio inviato!", description: "Ti risponderemo il prima possibile." });
    } catch {
      toast({ title: "Errore", description: "Si è verificato un errore. Riprova.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="py-10 sm:py-16 px-4" style={{ backgroundColor: 'var(--tp-black)', minHeight: '80vh' }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-2 text-white"
          style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>CONTATTI</h1>
        <div className="w-20 h-1 mx-auto mb-8 sm:mb-12" style={{ backgroundColor: 'var(--tp-red)' }} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
          {/* Contact Info */}
          <div className="space-y-4 sm:space-y-6 order-2 lg:order-1">
            <div className="p-5 sm:p-6 rounded-lg" style={{ backgroundColor: 'var(--tp-dark)' }}>
              <h3 className="text-white font-bold mb-4 tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>SCRIVICI</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Phone size={18} className="text-red-500 mt-1 flex-shrink-0" />
                  <div><p className="text-gray-400 text-xs mb-1">CHIAMACI</p><p className="text-white text-sm">Segreteria +39 011 2341 6793</p></div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail size={18} className="text-red-500 mt-1 flex-shrink-0" />
                  <div><p className="text-gray-400 text-xs mb-1">EMAIL</p><a href="mailto:info@tecnicheperfette.com" className="text-white text-sm hover:text-red-400 transition-colors break-all">info@tecnicheperfette.com</a></div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={18} className="text-red-500 mt-1 flex-shrink-0" />
                  <div><p className="text-gray-400 text-xs mb-1">ORARI</p><p className="text-white text-sm">DALLE 9 ALLE 18</p></div>
                </div>
              </div>
            </div>
            <div className="p-5 sm:p-6 rounded-lg" style={{ backgroundColor: 'var(--tp-dark)' }}>
              <h3 className="text-white font-bold mb-4 tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>SOCIAL</h3>
              <div className="space-y-3">
                <a href="https://www.instagram.com/tecnicheperfette/" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded bg-gray-800 hover:bg-gray-700 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">IG</span>
                  </div>
                  <div className="min-w-0"><p className="text-white text-sm font-medium">Instagram</p><p className="text-gray-400 text-xs truncate">@tecnicheperfette</p></div>
                </a>
                <a href="https://www.tiktok.com/@tecnicheperfette" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded bg-gray-800 hover:bg-gray-700 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-black border border-gray-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">TT</span>
                  </div>
                  <div className="min-w-0"><p className="text-white text-sm font-medium">TikTok</p><p className="text-gray-400 text-xs truncate">@tecnicheperfette</p></div>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            {sent ? (
              <div className="text-center py-12 sm:py-16 rounded-lg" style={{ backgroundColor: 'var(--tp-dark)' }}>
                <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Messaggio inviato!</h3>
                <p className="text-gray-400 mb-6 text-sm sm:text-base">Chiamaci e contattaci per ogni info</p>
                <Button onClick={() => { setSent(false); setFormData({ nome: '', email: '', oggetto: '', messaggio: '' }); setFiles([]); }}
                  className="bg-red-600 hover:bg-red-700 text-white">Invia un altro messaggio</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 sm:p-8 rounded-lg space-y-4 sm:space-y-5" style={{ backgroundColor: 'var(--tp-dark)' }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>NOME *</label>
                    <Input value={formData.nome} onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))}
                      className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-600 focus:border-red-500" placeholder="Il tuo nome" />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>EMAIL *</label>
                    <Input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                      className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-600 focus:border-red-500" placeholder="La tua email" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>OGGETTO</label>
                  <Input value={formData.oggetto} onChange={e => setFormData(p => ({ ...p, oggetto: e.target.value }))}
                    className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-600 focus:border-red-500" placeholder="Oggetto del messaggio" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>MESSAGGIO *</label>
                  <Textarea value={formData.messaggio} onChange={e => setFormData(p => ({ ...p, messaggio: e.target.value }))}
                    className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-600 focus:border-red-500 min-h-[120px] sm:min-h-[150px]" placeholder="Scrivi il tuo messaggio..." />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-2 tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>ALLEGA FILE</label>
                  <label className="flex items-center justify-center gap-2 p-3 sm:p-4 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-red-500 transition-colors">
                    <Upload size={20} className="text-gray-500" />
                    <span className="text-gray-500 text-sm">Clicca per caricare i tuoi file</span>
                    <input type="file" multiple className="hidden" onChange={handleFileChange} />
                  </label>
                  {files.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {files.map((file, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-800 rounded text-sm">
                          <span className="text-gray-300 truncate mr-2">{file.name}</span>
                          <button type="button" onClick={() => removeFile(i)} className="text-red-400 hover:text-red-300 text-xs whitespace-nowrap">Rimuovi</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <PrivacyCheckbox
                  checked={privacyAccepted}
                  onCheckedChange={setPrivacyAccepted}
                  formType="contatto"
                />
                <Button type="submit" disabled={loading} className="w-full py-5 sm:py-6 text-base sm:text-lg font-bold tracking-wider text-white"
                  style={{ backgroundColor: 'var(--tp-red)', fontFamily: 'Oswald, sans-serif' }}>
                  <Send size={18} className="mr-2" /> {loading ? 'INVIO IN CORSO...' : 'INVIA MESSAGGIO'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContattiPage;
